'use client';

import { c as _c } from "react/compiler-runtime";
export type BuyBoxMode = 'single' | 'bundle';

/**
 * Outline stick-pack pouch: flat-bottom bag with a top seal band. The body is
 * filled with the tab background so overlapped pouches occlude the one behind.
 */
function PouchIcon(t0) {
  const $ = _c(6);
  const {
    className,
    bodyFillClass
  } = t0;
  let t1;
  if ($[0] !== bodyFillClass) {
    t1 = <path className={bodyFillClass} d="M1.5 21.5L20.5 21.5L21 6L21 0.5L1 0.499999L0.961431 6L1.5 21.5Z" />;
    $[0] = bodyFillClass;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <path fill="none" d="M0.961235 6L21 6L21 0.5L0.961235 0.499999L0.961235 6Z" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== className || $[4] !== t1) {
    t3 = <svg viewBox="0 0 22 22" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">{t1}{t2}</svg>;
    $[3] = className;
    $[4] = t1;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}

/** An overlapped row of pouch outlines mirroring how many bags the option contains. */
function PouchRow(t0) {
  const $ = _c(8);
  const {
    count,
    active
  } = t0;
  const t1 = `flex flex-shrink-0 ${active ? "text-neutral-900" : "text-neutral-400"}`;
  let t2;
  if ($[0] !== active || $[1] !== count) {
    let t3;
    if ($[3] !== active) {
      t3 = (_, index) => <PouchIcon key={index} className={`h-4 w-4 ${index > 0 ? "-ml-[7px]" : ""}`} bodyFillClass={active ? "fill-neutral-50" : "fill-white"} />;
      $[3] = active;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    t2 = Array.from({
      length: count
    }, t3);
    $[0] = active;
    $[1] = count;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <span className={t1} aria-hidden="true">{t2}</span>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}

/**
 * Buy-box mode switch: one bag vs the Crew Pack bundle, each with pouch
 * icons, pack count, and per-stick price so the bundle's value is legible
 * at a glance.
 */
export function BuyBoxModeTabs(t0) {
  const $ = _c(41);
  const {
    mode,
    onChange,
    oneBoxLabel,
    oneBoxMeta,
    bundleLabel,
    bundleMeta,
    bestValueBadge,
    bundleBagCount
  } = t0;
  const tabClass = _temp;
  const t1 = mode === "single";
  const t2 = tabClass(mode === "single");
  let t3;
  if ($[0] !== onChange) {
    t3 = () => onChange("single");
    $[0] = onChange;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const t4 = mode === "single";
  let t5;
  if ($[2] !== t4) {
    t5 = <PouchRow count={1} active={t4} />;
    $[2] = t4;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== oneBoxLabel) {
    t6 = <span className="block text-[12px] font-bold uppercase leading-tight tracking-wide text-neutral-900">{oneBoxLabel}</span>;
    $[4] = oneBoxLabel;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== oneBoxMeta) {
    t7 = <span className="block truncate text-[10px] leading-tight text-neutral-500">{oneBoxMeta}</span>;
    $[6] = oneBoxMeta;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== t6 || $[9] !== t7) {
    t8 = <span className="min-w-0">{t6}{t7}</span>;
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t1 || $[12] !== t2 || $[13] !== t3 || $[14] !== t5 || $[15] !== t8) {
    t9 = <button type="button" role="tab" aria-selected={t1} className={t2} onClick={t3}>{t5}{t8}</button>;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
    $[14] = t5;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const t10 = mode === "bundle";
  const t11 = tabClass(mode === "bundle");
  let t12;
  if ($[17] !== onChange) {
    t12 = () => onChange("bundle");
    $[17] = onChange;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== bestValueBadge) {
    t13 = <span className="absolute -top-[9px] left-2.5 bg-brand-accent px-1.5 py-0.5 text-[9px] font-bold uppercase leading-none tracking-[0.06em] text-white">{bestValueBadge}</span>;
    $[19] = bestValueBadge;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  const t14 = mode === "bundle";
  let t15;
  if ($[21] !== bundleBagCount || $[22] !== t14) {
    t15 = <PouchRow count={bundleBagCount} active={t14} />;
    $[21] = bundleBagCount;
    $[22] = t14;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== bundleLabel) {
    t16 = <span className="block text-[12px] font-bold uppercase leading-tight tracking-wide text-neutral-900">{bundleLabel}</span>;
    $[24] = bundleLabel;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] !== bundleMeta) {
    t17 = <span className="block truncate text-[10px] leading-tight text-neutral-500">{bundleMeta}</span>;
    $[26] = bundleMeta;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] !== t16 || $[29] !== t17) {
    t18 = <span className="min-w-0">{t16}{t17}</span>;
    $[28] = t16;
    $[29] = t17;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] !== t10 || $[32] !== t11 || $[33] !== t12 || $[34] !== t13 || $[35] !== t15 || $[36] !== t18) {
    t19 = <button type="button" role="tab" aria-selected={t10} className={t11} onClick={t12}>{t13}{t15}{t18}</button>;
    $[31] = t10;
    $[32] = t11;
    $[33] = t12;
    $[34] = t13;
    $[35] = t15;
    $[36] = t18;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  let t20;
  if ($[38] !== t19 || $[39] !== t9) {
    t20 = <div className="flex gap-2" role="tablist" aria-label="Purchase format">{t9}{t19}</div>;
    $[38] = t19;
    $[39] = t9;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  return t20;
}
function _temp(active) {
  return `relative flex flex-1 items-center gap-2.5 rounded border px-2.5 py-2 text-left transition-colors ${active ? "border-neutral-900 ring-1 ring-neutral-900 bg-neutral-50" : "border-neutral-300 hover:border-neutral-400"}`;
}